import { useState, FormEvent, useMemo, useEffect } from "react";
import Form from "react-bootstrap/Form";
import Button from "react-bootstrap/Button";
import { resources } from "@constants/hardcoded-strings";
import { MONTH_DAY_YEAR_INPUT_DATE_FORMAT } from "@constants/date-formats";
import { LabelWidthTypes } from "@enums/label-width-types";
import { LayoutTypes } from "@enums/layout-types";
import { ButtonTypes } from "@enums/button-types";
import { CustomizeBySearchTypes } from "@enums/customize-by-search-types";
import { TimeRangeTypes } from "@enums/time-range-types";
import { SelectOption } from "@idw-types/common";
import { AxnRelyingParty, AxnCompany, SimpleAxnWorkflow } from "@idw-types/data/reference-data/axn";
import { RpServiceConfiguration } from "@idw-types/data/rp-service-configuration";
import { WorkflowUsageDetailsSearchFiltersState } from "@idw-types/state/workflow-usage-details-state";
import { initialWorkflowUsageDetailsState } from "@redux/reducers/workflow-usage-details-reducer";
import { getTimeRange, getTimeRangeLabel } from "@utils/date-util";
import {
    getRelyingPartyOptions,
    getRpServiceOptionsForCompanyAndRelyingParty,
    getWorkflowOptionsForCompanyAndRelyingParty
} from "@utils/search-form/workflow-usage-details/workflow-usage-details-search-form-util";
import { mapEntityWithNameAndIdToSelectOption, mapRpServiceToSelectOption } from "@utils/select-option-mapping-util";
import { ButtonWithSpinner } from "../../../common/button-with-spinner/button-with-spinner";
import { OrganizationSelectFormGroup } from "../../../common/select-form-groups";
import { SearchFormFilterRow } from "../../../common/search-form-filter-row/search-form-filter-row";
import { SelectFormGroup } from "../../../common/select-form-group/select-form-group";
import { RadioInputFormGroup } from "../../../common/radio-input-form-group/radio-input-form-group";
import { TimeRangeSearchFormFilterRow } from "../../../common/time-range-search-form-filter-row/time-range-search-form-filter-row";
import { SearchFormHeaderContainer } from "../../../common/search-form-header-container/search-form-header-container";
import styles from "./workflow-usage-details-search-form.less";

type WorkflowUsageDetailsSearchFormProps = {
    requestInProgress: boolean;
    companies: Array<AxnCompany>;
    relyingParties: Array<AxnRelyingParty>;
    rpServices: Array<RpServiceConfiguration>;
    workflows: Array<SimpleAxnWorkflow>;
    searchFilters: WorkflowUsageDetailsSearchFiltersState;
    handleSearch: (searchFilters: WorkflowUsageDetailsSearchFiltersState) => void;
};

type WorkflowUsageDetailsSearchFormState = {
    searchType: CustomizeBySearchTypes;
    selectedCompanyOption: SelectOption;
    selectedRelyingPartyOption: SelectOption;
    selectedRpServiceOption: SelectOption;
    selectedWorkflowOption: SelectOption;
    selectedTimeRange: SelectOption;
    startDate: Date;
    endDate: Date;
};

export const WorkflowUsageDetailsSearchForm = (props: WorkflowUsageDetailsSearchFormProps) => {
    const maxDate = useMemo(() => new Date(), []);
    const minDate = useMemo(() => new Date(2015, 0, 1), []);
    const [labelWidth] = useState(LabelWidthTypes.EXTRA_SMALL);
    const [layout] = useState(LayoutTypes.COLUMN);

    const {
        requestInProgress,
        companies,
        relyingParties,
        rpServices,
        workflows,
        searchFilters,
        handleSearch
    } = props;

    const getFilterStateFromSearchFilters = (searchFilters: WorkflowUsageDetailsSearchFiltersState): WorkflowUsageDetailsSearchFormState => {
        let selectedCompany = companies.find(company => searchFilters.companyId === company.id);

        if (companies.length === 1) {
            selectedCompany = companies[0];
        }

        let selectedRelyingParty = relyingParties.find(rp => searchFilters.relyingPartyId === rp.id);

        if (relyingParties.length === 1) {
            selectedRelyingParty = relyingParties[0];
        }

        const selectedWorkflow = workflows.find(workflow => searchFilters.workflowId === workflow.id);
        const selectedRpService = searchFilters.rpServiceId ? rpServices.find(rpService => searchFilters.rpServiceId === rpService.id) : null;

        return {
            searchType: searchFilters.customizeBySearchType,
            selectedCompanyOption: selectedCompany ? mapEntityWithNameAndIdToSelectOption(selectedCompany) : null,
            selectedRpServiceOption: selectedRpService ? mapRpServiceToSelectOption(selectedRpService) : null,
            selectedWorkflowOption: selectedWorkflow ? mapEntityWithNameAndIdToSelectOption(selectedWorkflow) : null,
            selectedTimeRange: {
                label: getTimeRangeLabel(searchFilters.timeRangeType),
                value: searchFilters.timeRangeType
            },
            startDate: searchFilters.startDate,
            endDate: searchFilters.endDate,
            selectedRelyingPartyOption: selectedRelyingParty ? mapEntityWithNameAndIdToSelectOption(selectedRelyingParty) : null
        };
    };

    const [showFilters, setShowFilters] = useState(true);
    const [filterState, setFilterState] = useState<WorkflowUsageDetailsSearchFormState>(getFilterStateFromSearchFilters(searchFilters));

    useEffect(() => {
        setFilterState(getFilterStateFromSearchFilters(searchFilters));
    }, [searchFilters]);

    const clearFilterState = () => {
        setFilterState(getFilterStateFromSearchFilters(initialWorkflowUsageDetailsState.searchFilters));
    };

    const toggleShowFilters = () => {
        setShowFilters(!showFilters);
    };

    const handleSubmit = (event: FormEvent) => {
        event.preventDefault();
        const companyId = filterState.selectedCompanyOption?.value || null;
        const relyingPartyId = filterState.selectedRelyingPartyOption?.value || null;
        const rpServiceId = filterState.searchType === CustomizeBySearchTypes.VERIFICATION_SERVICE ? (filterState.selectedRpServiceOption?.value || null) : null;
        const workflowId = filterState.searchType === CustomizeBySearchTypes.WORKFLOW ? (filterState.selectedWorkflowOption?.value || null) : null;
        handleSearch({
            customizeBySearchType: filterState.searchType,
            companyId,
            relyingPartyId,
            rpServiceId,
            workflowId,
            timeRangeType: filterState.selectedTimeRange.value as TimeRangeTypes,
            startDate: filterState.startDate,
            endDate: filterState.endDate,
        });
    };

    const handleUpdateSearchType = (newValue: string) => {
        setFilterState({
            ...filterState,
            searchType: newValue as CustomizeBySearchTypes
        });
    };

    const handleUpdateCompanyOption = (newValue: SelectOption) => {
        let selectedRelyingPartyOption = null;
        let selectedRpServiceOption = null;
        let selectedWorkflowOption = null;
        if (newValue) {
            if (filterState.selectedRelyingPartyOption) {
                const selectedRelyingParty = relyingParties.find(rp => rp.id === filterState.selectedRelyingPartyOption.value);
                if (selectedRelyingParty && selectedRelyingParty.companyID === newValue.value) {
                    selectedRelyingPartyOption = filterState.selectedRelyingPartyOption;
                }
            }
            if (filterState.selectedRpServiceOption) {
                const selectedRpService = rpServices.find(service => service.id === filterState.selectedRpServiceOption.value);
                if (selectedRpService) {
                    const rpServiceRelyingParty = relyingParties.find(rp => rp.id === selectedRpService.relyingPartyID);
                    if (rpServiceRelyingParty && rpServiceRelyingParty.companyID === newValue.value) {
                        selectedRpServiceOption = filterState.selectedRpServiceOption;
                    }
                }
            }
            if (filterState.selectedWorkflowOption) {
                const selectedWorkflow = workflows.find(workflow => workflow.id === filterState.selectedWorkflowOption.value);
                if (selectedWorkflow && selectedWorkflow.companyID === newValue.value) {
                    selectedWorkflowOption = filterState.selectedWorkflowOption;
                }
            }
        } else {
            selectedRelyingPartyOption = filterState.selectedRelyingPartyOption;
            selectedRpServiceOption = filterState.selectedRpServiceOption;
            selectedWorkflowOption = filterState.selectedWorkflowOption;
        }

        setFilterState({
            ...filterState,
            selectedCompanyOption: newValue,
            selectedRpServiceOption: selectedRpServiceOption,
            selectedWorkflowOption: selectedWorkflowOption,
            selectedRelyingPartyOption: selectedRelyingPartyOption
        });
    };

    const handleUpdateRpServiceOption = (newValue: SelectOption) => {
        setFilterState({
            ...filterState,
            selectedRpServiceOption: newValue
        });
    };

    const handleUpdateWorkflowOption = (newValue: SelectOption) => {
        setFilterState({
            ...filterState,
            selectedWorkflowOption: newValue
        });
    };

    const handleTimeRangeChange = (newValue: SelectOption) => {
        if (newValue.value === TimeRangeTypes.CUSTOM) {
            setFilterState({
                ...filterState,
                selectedTimeRange: newValue
            });
        } else {
            setFilterState({
                ...filterState,
                selectedTimeRange: newValue,
                ...getTimeRange(newValue.value as TimeRangeTypes)
            });
        }
    };

    const handleStartDateChange = (newStartDate: Date) => {
        setFilterState({
            ...filterState,
            startDate: newStartDate
        });
    };

    const handleEndDateChange = (newEndDate: Date) => {
        setFilterState({
            ...filterState,
            endDate: newEndDate
        });
    };

    const handleUpdateSelectedRelyingPartyOption = (newValue: SelectOption) => {
        let selectedRpServiceOption = null;
        let selectedWorkflowOption = null;
        if (newValue) {
            if (filterState.selectedRpServiceOption) {
                const selectedRpService = rpServices.find(service => service.id === filterState.selectedRpServiceOption.value);
                if (selectedRpService && selectedRpService.relyingPartyID === newValue.value) {
                    selectedRpServiceOption = filterState.selectedRpServiceOption;
                }
            }
            if (filterState.selectedWorkflowOption) {
                const selectedWorkflow = workflows.find(workflow => workflow.id === filterState.selectedWorkflowOption.value);
                if (selectedWorkflow && selectedWorkflow.relyingPartyID === newValue.value) {
                    selectedWorkflowOption = filterState.selectedWorkflowOption;
                }
            }
        } else {
            selectedRpServiceOption = filterState.selectedRpServiceOption;
            selectedWorkflowOption = filterState.selectedWorkflowOption;
        }

        setFilterState({
            ...filterState,
            selectedRelyingPartyOption: newValue,
            selectedRpServiceOption: selectedRpServiceOption,
            selectedWorkflowOption: selectedWorkflowOption

        });
    };

    const searchTypeOptions: Array<SelectOption> = useMemo(() => {
        return [
            { label: "Workflow", value: CustomizeBySearchTypes.WORKFLOW },
            { label: "Verification Service", value: CustomizeBySearchTypes.VERIFICATION_SERVICE }
        ];
    }, []);

    const relyingPartyOptions: Array<SelectOption> = useMemo(() => {
        return getRelyingPartyOptions(relyingParties, filterState.selectedCompanyOption);
    }, [
        filterState.selectedCompanyOption,
        relyingParties
    ]);

    const rpServiceOptions: Array<SelectOption> = useMemo(() => {
        return getRpServiceOptionsForCompanyAndRelyingParty(rpServices, relyingParties, filterState.selectedCompanyOption, filterState.selectedRelyingPartyOption);
    }, [filterState.selectedCompanyOption, relyingParties, rpServices, filterState.selectedRelyingPartyOption]);

    const workflowOptions: Array<SelectOption> = useMemo(() => {
        return getWorkflowOptionsForCompanyAndRelyingParty(workflows, filterState.selectedCompanyOption, filterState.selectedRelyingPartyOption);
    }, [filterState.selectedCompanyOption, filterState.selectedRelyingPartyOption, relyingParties, workflows]);

    const timeRangeTypes = [
        TimeRangeTypes.CURRENT_MONTH,
        TimeRangeTypes.LAST_MONTH,
        TimeRangeTypes.CURRENT_QUARTER,
        TimeRangeTypes.LAST_QUARTER,
        TimeRangeTypes.CURRENT_YEAR,
        TimeRangeTypes.LAST_YEAR,
        TimeRangeTypes.CUSTOM
    ];

    const noServicesOptionsMessage = () => {
        return <span className={styles.redText}>{resources.common.searchForm.filters.verificationServices.noOptionsMessage}</span>;
    };

    const noWorkflowsOptionsMessage = () => {
        return <span className={styles.redText}>{resources.common.searchForm.filters.workflows.noOptionsMessage}</span>;
    };

    return (
        <Form className={styles.workflowUsageDetailsSearchForm} onSubmit={handleSubmit}>
            <SearchFormHeaderContainer
                idPrefix={"workflow-usage-details"}
                showFilters={showFilters}
                showToggle={true}
                toggleShowFilters={toggleShowFilters}
            />
            {showFilters && (
                <>
                    <SearchFormFilterRow>
                        <RadioInputFormGroup
                            controlId={"workflow-usage-details-search-filters-search-type-input"}
                            automationId={"workflow-usage-details-search-filters-search-type-input"}
                            disabled={false}
                            isRequired={false}
                            label={resources.common.searchForm.filters.customizeBy.label}
                            onChange={handleUpdateSearchType}
                            options={searchTypeOptions}
                            value={filterState.searchType}
                        />
                    </SearchFormFilterRow>
                    <SearchFormFilterRow>
                        <OrganizationSelectFormGroup
                            idPrefix="workflow-usage-details"
                            axnCompanies={companies}
                            isClearable={companies.length > 1}
                            isRequired={false}
                            layout={layout}
                            isSearchable={true}
                            labelWidth={labelWidth}
                            selectedOption={filterState.selectedCompanyOption}
                            onChange={handleUpdateCompanyOption}
                            disabled={companies.length === 1}
                        />

                        <SelectFormGroup
                            automationId={`verification-transactions-search-filters-relying-party-select`}
                            controlId={`verification-transactions-search-filters-relying-party-select`}
                            label={resources.common.searchForm.filters.relyingParty.label}
                            ariaLabel={resources.common.searchForm.filters.relyingParty.label}
                            onChange={handleUpdateSelectedRelyingPartyOption}
                            options={relyingPartyOptions}
                            selectedOption={filterState.selectedRelyingPartyOption}
                            disabled={relyingParties.length === 1}
                            isRequired={false}
                            isSearchable={true}
                            isClearable={relyingParties.length > 1}
                            labelWidth={labelWidth}
                            layout={layout}
                        />
                        {filterState.searchType === CustomizeBySearchTypes.VERIFICATION_SERVICE && (
                            <SelectFormGroup
                                automationId={`workflow-usage-details-search-filters-service-select`}
                                controlId={`workflow-usage-details-search-filters-service-select`}
                                label={resources.common.searchForm.filters.verificationServices.label}
                                ariaLabel={resources.common.searchForm.filters.verificationServices.label}
                                onChange={handleUpdateRpServiceOption}
                                options={rpServiceOptions}
                                selectedOption={filterState.selectedRpServiceOption}
                                disabled={false}
                                isRequired={true}
                                isSearchable={true}
                                isClearable={true}
                                labelWidth={labelWidth}
                                layout={layout}
                                noOptionsMessage={noServicesOptionsMessage}
                            />
                        )}
                        {filterState.searchType === CustomizeBySearchTypes.WORKFLOW && (
                            <SelectFormGroup
                                automationId={`workflow-usage-details-search-filters-workflow-select`}
                                controlId={`workflow-usage-details-search-filters-workflow-select`}
                                label={resources.common.searchForm.filters.workflow.label}
                                ariaLabel={resources.common.searchForm.filters.workflow.label}
                                onChange={handleUpdateWorkflowOption}
                                options={workflowOptions}
                                selectedOption={filterState.selectedWorkflowOption}
                                disabled={false}
                                isRequired={true}
                                isSearchable={true}
                                isClearable={true}
                                labelWidth={labelWidth}
                                layout={layout}
                                noOptionsMessage={noWorkflowsOptionsMessage}
                            />
                        )}
                    </SearchFormFilterRow>
                    <TimeRangeSearchFormFilterRow
                        idPrefix={"workflow-usage-details-search-filters"}
                        labelWidth={labelWidth}
                        layout={layout}
                        onChangeTimeRange={handleTimeRangeChange}
                        onChangeStartDate={handleStartDateChange}
                        onChangeEndDate={handleEndDateChange}
                        selectedTimeRange={filterState.selectedTimeRange}
                        timeRangeTypes={timeRangeTypes}
                        startDateLabel={resources.common.searchForm.filters.startDate.label}
                        startDateAriaLabel={resources.common.searchForm.filters.startDate.label}
                        endDateLabel={resources.common.searchForm.filters.endDate.label}
                        endDateAriaLabel={resources.common.searchForm.filters.endDate.label}
                        minDate={minDate}
                        maxDate={maxDate}
                        dateFormat={MONTH_DAY_YEAR_INPUT_DATE_FORMAT}
                        startDate={filterState.startDate}
                        endDate={filterState.endDate}
                        showMonthDropdown={true}
                        showYearDropdown={true}
                        showMonthYearPicker={false}

                    />
                    <div className={styles.buttonDiv}>
                        <Button id="workflow-usage-details-clear-button" variant={"outline-secondary"} onClick={clearFilterState}>{resources.common.buttons.clear.label}</Button>
                        <ButtonWithSpinner
                            id="workflow-usage-details-load-button"
                            disabled={requestInProgress}
                            hideSpinner={false}
                            variant="primary"
                            type={ButtonTypes.SUBMIT}
                            label={resources.common.buttons.load.label}
                        />
                    </div>
                </>
            )}
        </Form>
    );
};